// Scenario Types for DEFCON TERMINAL

export type NodeType = 'narrative' | 'choice' | 'multi-select' | 'timed' | 'consequence' | 'ending';

export type OutcomeType =
  // Nuclear outcomes
  | 'PEACEFUL_RESOLUTION'
  | 'LIMITED_CONVENTIONAL'
  | 'PROXY_CONFLICT'
  | 'TACTICAL_NUCLEAR'
  | 'LIMITED_EXCHANGE'
  | 'MAJOR_EXCHANGE'
  | 'FULL_THERMONUCLEAR'
  // Covert outcomes
  | 'MISSION_SUCCESS'
  | 'PARTIAL_SUCCESS'
  | 'COMPROMISED'
  | 'DIPLOMATIC_INCIDENT'
  | 'ESCALATION_TO_CONFLICT'
  | 'BLOWN_OPERATION'
  // Additional outcomes
  | 'HOLLOW_VICTORY'
  | 'STRATEGIC_DEFEAT';

export type PathType = 'sarn-west' | 'sarn-east' | 'phantom-directive' | 'red-shadow';

export type CategoryType =
  // SARN-WEST categories
  | 'crisis-escalation'
  | 'first-strike'
  | 'accidental-war'
  | 'limited-exchange'
  | 'full-scale'
  | 'escalation'
  | 'accident'
  | 'retaliation'
  | 'diplomatic-failure'
  // SARN-EAST categories
  | 'defensive-posture'
  | 'offensive-planning'
  | 'retaliatory'
  | 'counter-strike'
  | 'crisis'
  | 'diplomatic'
  | 'internal-crisis'
  | 'proxy-war'
  | 'intelligence'
  | 'endgame'
  // PHANTOM DIRECTIVE categories
  | 'destabilization'
  | 'intelligence-extraction'
  | 'direct-action'
  | 'counter-intelligence'
  | 'special-recon'
  // RED SHADOW categories
  | 'active-measures'
  | 'illegal-networks'
  | 'tech-acquisition'
  | 'elimination'
  | 'counter-ops'
  | 'regional-conflict'
  | 'sabotage'
  | 'assassination';

export interface ScenarioState {
  defcon: number; // 1-5
  worldTension: number; // 0-100
  alliedMorale: number; // 0-100
  sovietAggression: number; // 0-100
  date: string; // YYYY-MM-DD
  time: string; // HH:MM:SSZ
  customVariables?: Record<string, number | string | boolean>;
}

export interface StateChange {
  defcon?: number | string; // Can be absolute or relative ("+1", "-1")
  worldTension?: number | string;
  alliedMorale?: number | string;
  sovietAggression?: number | string;
  time?: string; // Relative time change ("+2h", "+30m")
  date?: string; // Relative date change ("+1d")
  customVariables?: Record<string, number | string | boolean>;
}

export interface ScoreInfo {
  base: number;
  tags?: string[];
  multiplier?: number;
  bonuses?: string[];
  penalties?: string[];
}

export interface ChoiceOption {
  id: string;
  text: string;
  next: string; // Node ID to navigate to
  stateChanges?: StateChange;
  score?: ScoreInfo;
  condition?: string; // Optional condition for displaying this option
  hidden?: boolean; // Easter egg options
}

export interface NarrativeNode {
  type: 'narrative';
  content: string[];
  next: string;
  ascii?: string;
  stateChanges?: StateChange;
  delay?: number; // Typing delay in ms
}

export interface ChoiceNode {
  type: 'choice';
  prompt: string;
  content?: string[];
  options: ChoiceOption[];
  ascii?: string;
  timeLimit?: number; // For timed choices, in seconds
}

export interface MultiSelectNode {
  type: 'multi-select';
  prompt: string;
  content?: string[];
  options: ChoiceOption[];
  minSelections?: number;
  maxSelections?: number;
  ascii?: string;
}

export interface TimedNode {
  type: 'timed';
  prompt: string;
  content?: string[];
  options: ChoiceOption[];
  timeLimit: number; // Seconds
  defaultOption: string; // Option ID if time runs out
  ascii?: string;
}

export interface ConsequenceNode {
  type: 'consequence';
  content: string[];
  next: string;
  ascii?: string;
  stateChanges?: StateChange;
  animation?: string; // Animation to play
}

export interface EndingNode {
  type: 'ending';
  outcomeType: OutcomeType;
  content: string[];
  ascii?: string;
  score: ScoreInfo;
  animation?: string;
  casualties?: number;
  warheadsUsed?: number;
  // Interactive launch configuration
  interactiveLaunch?: boolean;
  targetDescription?: string;
}

export type ScenarioNode =
  | NarrativeNode
  | ChoiceNode
  | MultiSelectNode
  | TimedNode
  | ConsequenceNode
  | EndingNode;

export interface EasterEgg {
  id: string;
  triggerNode: string;
  triggerChoice?: string;
  triggerCommand?: string;
  content: string;
  points: number;
}

export interface Scenario {
  $schema?: string;
  id: string;
  title: string;
  path: PathType;
  category: CategoryType;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'extreme';
  minimumDecisions: number;
  stepsToFullNuclearWar?: number; // Only for nuclear paths

  initialState: ScenarioState;
  nodes: Record<string, ScenarioNode>;
  startNode: string;

  easterEggs?: EasterEgg[];

  metadata?: {
    author?: string;
    version?: string;
    lastUpdated?: string;
    historicalBasis?: string;
    movieReferences?: string[];
  };
}

// Validation helpers
export function isNuclearPath(path: PathType): boolean {
  return path === 'sarn-west' || path === 'sarn-east';
}

export function isCovertPath(path: PathType): boolean {
  return path === 'phantom-directive' || path === 'red-shadow';
}

export function getPathDisplayName(path: PathType): string {
  const names: Record<PathType, string> = {
    'sarn-west': 'SARN-WEST (Strategic Command)',
    'sarn-east': 'PERIMETR-2 (Eastern Command)',
    'phantom-directive': 'PHANTOM DIRECTIVE',
    'red-shadow': 'RED SHADOW',
  };
  return names[path];
}

export function getOutcomeMultiplier(outcome: OutcomeType): number {
  const multipliers: Record<OutcomeType, number> = {
    PEACEFUL_RESOLUTION: 1.0,
    LIMITED_CONVENTIONAL: 1.2,
    PROXY_CONFLICT: 1.3,
    TACTICAL_NUCLEAR: 1.5,
    LIMITED_EXCHANGE: 1.8,
    MAJOR_EXCHANGE: 2.0,
    FULL_THERMONUCLEAR: 2.5,
    MISSION_SUCCESS: 1.0,
    PARTIAL_SUCCESS: 1.1,
    COMPROMISED: 1.3,
    DIPLOMATIC_INCIDENT: 1.5,
    ESCALATION_TO_CONFLICT: 1.8,
    BLOWN_OPERATION: 2.0,
    HOLLOW_VICTORY: 0.7,
    STRATEGIC_DEFEAT: 0.3,
  };
  return multipliers[outcome] ?? 0.5;
}
